<template>
  <v-app id="inspire">
    <v-app-bar app color="dark" dark>
      <v-toolbar-title>Graph Layout Labelling</v-toolbar-title>
      <v-spacer></v-spacer>
      <span v-if="mainStudyTotal" class="mr-4 font-weight-medium text-h6">
        {{ mainStudyCurrent }} / {{ mainStudyTotal }}
      </span>
      <v-btn
        v-if="showCompleteShortcut"
        color="success"
        class="ma-2"
        @click="goToFinish"
      >
        Complete
      </v-btn>
    </v-app-bar>

    <v-dialog v-model="idleDialog" persistent max-width="500">
      <v-card>
        <v-card-title class="text-h5">Are you still there?</v-card-title>
        <v-card-text>
          You haven't answered in {{ idleTimeoutSeconds }} seconds. This trial will be skipped and not recorded.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="confirmIdleSkip">Continue to next graph</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-main>
      <div>
        <div class="text-center mb-1">
          <h2 class="text-h6">Please answer the following questions about this graph:</h2>
        </div>

        <v-row class="mb-2">
          <v-col cols="12" class="text-center py-1">
            <v-card class="mx-auto study-banner">
              <v-img
                v-if="graph_image_url"
                :key="'graph-' + graph_id + '-' + graph_image_url"
                :src="graph_image_url"
                contain
                height="100%"
                width="100%"
                class="grey lighten-3"
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              <v-alert v-else-if="imageError" type="warning" dense class="ma-4">
                Could not load this graph image. You can still answer, or wait and refresh.
              </v-alert>
              <v-row v-else class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card class="mx-auto pa-3 study-answers">
              <div
                v-for="(question, index) in questions"
                :key="question.question_id"
                :class="index < questions.length - 1 ? 'answer-block' : ''"
              >
                <p
                  class="text-center mb-1"
                  :class="question.question_id === 1 ? 'path-prompt' : 'effort-prompt font-weight-medium'"
                >{{ question.question_text }}</p>
                <div v-if="question.question_type === 'button_choice'" class="button-choice-container">
                  <v-btn-toggle
                    v-model="answers[question.question_id]"
                    class="button-choice-group"
                    @change="() => handleAnswerClick(question.question_id)"
                  >
                    <v-btn
                      v-for="option in question.options"
                      :key="option"
                      :value="option"
                      color="primary"
                      outlined
                      class="choice-button"
                    >
                      {{ option }}
                    </v-btn>
                  </v-btn-toggle>
                </div>
                <div v-else-if="question.question_type === 'text'">
                  <v-textarea
                    v-model="answers[question.question_id]"
                    placeholder="Please provide your answer... (Press Enter to submit)"
                    rows="2"
                    outlined
                    dense
                    @keydown.enter.prevent="() => handleTextEnter(question.question_id)"
                  ></v-textarea>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import config from '@/config';
import prolificConfig from '@/config/prolific';
import idleDetection from '@/mixins/idleDetection';
import { isTestProlificPid } from '@/utils/prolific';

export default {
  name: 'ProlificQuestionLabelling',
  title: 'Prolific Study',
  mixins: [idleDetection],
  data() {
    return {
      sessionId: this.$route.params.sessionId,
      graph_id: this.$route.params.graphId,
      user_id: null,
      prolificPid: null,
      questions: [],
      answers: {},
      graph_image_url: '',
      imageError: false,
      start_time: null,
      isSubmitting: false,
      isInitializing: true,
      mainStudyCurrent: null,
      mainStudyTotal: null,
      idleTimeoutSeconds: prolificConfig.IDLE_TIMEOUT_SECONDS,
    };
  },
  computed: {
    showCompleteShortcut() {
      return isTestProlificPid(this.prolificPid);
    },
    isFormComplete() {
      return this.questions.length > 0 && this.questions.every(q => {
        const answer = this.answers[q.question_id];
        return answer !== undefined && answer !== null && answer !== '' &&
          (typeof answer !== 'string' || answer.trim() !== '');
      });
    },
  },
  async created() {
    this.start_time = Date.now();
    await this.loadSession();
    await this.loadStudyConfig();
    this.loadQuestions();
    this.loadGraphImage();
    this.registerIdleListeners();
  },
  watch: {
    '$route.params.graphId'(newId) {
      if (newId && String(newId) !== String(this.graph_id)) {
        this.resetForNewGraph(newId);
      }
    },
  },
  methods: {
    async loadSession() {
      const resp = await fetch(`${config.API_BASE_URL}/prolific/session/${this.sessionId}/`);
      const session = await resp.json();
      this.user_id = session.user_id;
      this.prolificPid = session.prolific_pid;
      this.mainStudyCurrent = session.main_study_current;
      this.mainStudyTotal = session.main_study_question_count;
      if (session.main_study_complete) {
        this.$router.push(`/prolific/study/${this.sessionId}/post-study`);
        return;
      }
      if (session.phase !== 'main') {
        this.$router.replace(`/prolific/study/${this.sessionId}/main`);
      }
    },
    goToFinish() {
      this.clearIdleTimer();
      this.$router.push(`/prolific/study/${this.sessionId}/post-study`);
    },
    async loadStudyConfig() {
      try {
        const resp = await fetch(`${config.API_BASE_URL}/study_config/`);
        const data = await resp.json();
        this.idleTimeoutSeconds = data.idle_timeout_seconds;
        if (!this.mainStudyTotal) {
          this.mainStudyTotal = data.main_study_question_count;
        }
      } catch (e) {
        console.warn(e);
      }
    },
    loadQuestions() {
      fetch(`${config.API_BASE_URL}/get_questions/`)
        .then(r => r.json())
        .then(data => {
          // Enforce pilot mental-effort scale 1–9 on question 2
          this.questions = data.map(q => {
            if (q.question_id === 2) {
              return {
                ...q,
                question_text: 'Rate the effort you devoted on a scale from 1 (lowest) to 9 (highest):',
                options: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
              };
            }
            return q;
          });
          this.questions.forEach(q => {
            this.$set(this.answers, q.question_id, null);
          });
          this.isInitializing = false;
        });
    },
    loadGraphImage() {
      this.graph_image_url = '';
      this.imageError = false;
      const gid = this.graph_id;
      fetch(`${config.API_BASE_URL}/get_graph_image/${gid}/`)
        .then(r => {
          if (!r.ok) throw new Error('get_graph_image failed');
          return r.json();
        })
        .then(data => {
          if (String(gid) !== String(this.graph_id)) return; // stale response
          const path = data.image_url || '';
          // Same-origin in production (empty API_BASE_URL); absolute path /Save/...
          this.graph_image_url = path.startsWith('http')
            ? path
            : `${config.API_BASE_URL || ''}${path}`;
        })
        .catch(() => {
          if (String(gid) === String(this.graph_id)) this.imageError = true;
        });
    },
    resetForNewGraph(graphId) {
      this.graph_id = graphId;
      this.start_time = Date.now();
      this.isSubmitting = false;
      this.imageError = false;
      this.graph_image_url = '';
      this.questions.forEach(q => {
        this.$set(this.answers, q.question_id, null);
      });
      this.loadGraphImage();
      this.resetIdleTimer();
    },
    confirmIdleSkip() {
      this.idleDialog = false;
      this.submitLabel(true);
    },
    handleIdleTimeout() {},
    submitLabel(isIdle = false) {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      const total_seconds = ((Date.now() - this.start_time) / 1000).toFixed(4);
      const answers_data = this.questions.map(q => ({
        question_id: q.question_id,
        answer: isIdle ? '' : (this.answers[q.question_id] || '').toString(),
        seconds_spent: total_seconds / Math.max(this.questions.length, 1),
      }));

      fetch(`${config.API_BASE_URL}/prolific/label_graph`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          graph_id: parseInt(this.graph_id),
          user_id: parseInt(this.user_id),
          answers: answers_data,
          participant_session_id: parseInt(this.sessionId),
          is_idle_timeout: isIdle,
        }),
      })
        .then(() => this.goToNextGraph())
        .catch(() => { this.isSubmitting = false; });
    },
    goToNextGraph() {
      fetch(`${config.API_BASE_URL}/prolific/session/${this.sessionId}/main/next_graph/`)
        .then(r => r.json())
        .then(data => {
          if (data.study_complete) {
            this.$router.push(`/prolific/study/${this.sessionId}/post-study`);
            return;
          }
          this.mainStudyCurrent = data.main_study_current;
          this.mainStudyTotal = data.main_study_question_count;
          this.$router.push(`/prolific/study/${this.sessionId}/label/${data.graph_id}`);
        })
        .catch(() => { this.isSubmitting = false; });
    },
    handleAnswerClick(questionId) {
      if (questionId) {
        this.resetIdleTimer();
        if (!this.isSubmitting && !this.isInitializing) {
          this.$nextTick(() => {
            if (this.isFormComplete) {
              setTimeout(() => this.submitLabel(false), 300);
            }
          });
        }
      }
    },
    handleTextEnter(questionId) {
      if (this.answers[questionId] && this.answers[questionId].trim()) {
        this.handleAnswerClick(questionId);
      }
    },
  },
};
</script>

<style scoped>
.study-banner {
  height: 56vh !important;
  width: 74.2vh !important;
  min-width: unset !important;
  max-width: 100% !important;
  margin: 0 auto !important;
}
.study-answers {
  width: 100%;
  max-width: 760px;
}
.answer-block {
  margin-bottom: 14px;
}
.path-prompt {
  font-size: 21px;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;
}
.effort-prompt {
  font-size: 21px;
  line-height: 1.25;
  white-space: nowrap;
}
.button-choice-container {
  text-align: center;
}

.button-choice-group {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 6px;
}

.choice-button {
  min-width: 48px !important;
  height: 48px !important;
  padding: 0 8px !important;
  font-size: 16px;
  font-weight: bold;
}

.choice-button.v-btn--active {
  background-color: #1976d2 !important;
  color: white !important;
}

.choice-button:hover {
  transform: scale(1.05);
  transition: transform 0.2s;
}
</style>
